<script setup lang="ts">
import { ref, computed } from 'vue';
import { useTemplateRefsList } from '@vueuse/core';
import { cloneDeep, pickBy } from 'lodash-es';

import { createFinancials, getBorrowerDisplayName, getEntityIncome } from '@/helpers/builder';
import { ApplicantType, EmploymentType, BorrowerType, TrusteeType } from '@/helpers/const';
import { formatList, getFullName } from '@/helpers/format';
import api from '@/api';
import { INVALID_JWT_ERROR, useSessionExpiry } from '@/helpers/useSessionExpiry';
import { useErhlCustomerLoanAppProcess } from '@/helpers/useErhlCustomerLoanAppProcess';
import { useCalcApi } from '@/helpers/useCalcApi';
import { useFinancialCollectionRules } from '@/helpers/useFinancialCollectionRules';
import { useHouseholdApplicants } from '@/helpers/useHouseholdApplicants';
import { useHouseholdMessages } from '@/helpers/useHouseholdMessages';
import { CheckIcon, XMarkIcon } from '@heroicons/vue/20/solid';

import IncomeForm from './modules/Income/IncomeForm.vue';
import CompanyIncomeForm from './modules/Income/CompanyIncomeForm.vue';
import IncomeStatusCard from './modules/Income/IncomeStatusCard.vue';

import type { IconName } from '@/helpers/icons';
import type { ApplicantFinancials, ApplicantId, CompanyIncome, EntityIncome, RetirementDetails } from '@/types';
import type { BorrowerGroup, CompanyDetails } from '@/types/BorrowerGroup';

const { authorisedUser, loanApp, loanApplicants, saveAndGoNext, goBack, send } = useErhlCustomerLoanAppProcess();
const { flagSessionExpired } = useSessionExpiry();
const calc = useCalcApi();
const isCalcProcessing = calc.isLoading;

const collectionRules = useFinancialCollectionRules(loanApp);

const isSecondaryDeclaration = false;
const isIncomeExpenseRequest = false;
const error = ref('');
const isLoading = ref(false);

const hasNonIndividualBorrower = computed(
  () =>
    loanApp.value.borrowerGroups?.some((g) => g.type === BorrowerType.COMPANY || g.type === BorrowerType.TRUST) ??
    false,
);

/** Names of the other applicants, whose income is requested from them via email */
const secondaryApplicantNames = computed(() =>
  formatList(
    loanApp.value.applicants
      .filter((a) => a.type === ApplicantType.SECONDARY && a.id !== authorisedUser.value?.id)
      .map(getFullName),
  ),
);

/** Hidden for a single applicant */
const incomeSourcesubHeading = computed(() => {
  if (hasNonIndividualBorrower.value) {
    return "Please enter all borrower income below by clicking 'Add income'";
  }

  if (loanApp.value.applicants.length <= 1) return '';

  return `Please enter your own income. We will request this information from ${secondaryApplicantNames.value} via email.`;
});

const showCommercialIncomeUI = computed(() => hasNonIndividualBorrower.value);

/** Check if company is registered (has ABN) and actively trading */
const isRegisteredAndTrading = (company?: CompanyDetails) => Boolean(company?.hasRegisteredABN && company?.isTrading);

/**
 * Determines if a borrower group should show Net Profit form.
 * Uses financial collection rules composable when in commercial flow.
 */
const shouldShowNetProfit = (group: BorrowerGroup): boolean => {
  // Use the new composable logic when commercial flow is enabled
  if (showCommercialIncomeUI.value) {
    return collectionRules.shouldShowNetProfitForGroup(group);
  }
  // Fallback to original logic
  if (group.type === BorrowerType.COMPANY) {
    return isRegisteredAndTrading(group.companyDetails);
  }
  if (group.type === BorrowerType.TRUST && group.trustDetails?.trusteeType === TrusteeType.COMPANY) {
    return isRegisteredAndTrading(group.trustDetails?.companyDetails);
  }
  return false;
};

/**
 * Determines if a borrower group should show individual income forms.
 * Uses financial collection rules for Unregulated scenarios.
 */
const shouldShowIndividualIncome = (group: BorrowerGroup): boolean => {
  if (showCommercialIncomeUI.value) {
    return collectionRules.shouldShowIndividualIncomeForGroup(group);
  }
  return true;
};

const getApplicantsForBorrowerGroup = (group: BorrowerGroup): ApplicantId[] => {
  return group.applicants?.map((a) => a.applicantId as ApplicantId) ?? [];
};

const { primaryPartner, otherApplicants, validApplicants, authorisedUserId } = useHouseholdApplicants(
  loanApp,
  authorisedUser,
  { isSecondaryDeclaration, isIncomeExpenseRequest },
);

useHouseholdMessages(
  { primaryPartner, otherApplicants, authorisedUserId },
  { isSecondaryDeclaration, isIncomeExpenseRequest },
);

const initFinancials = validApplicants.value.reduce((obj, item) => {
  obj[item] = createFinancials();
  return obj;
}, {} as ApplicantFinancials);

const validApplicantIds = new Set(loanApp.value.applicants.map((a) => a.id));
const existingFinancials = pickBy(cloneDeep(loanApp.value.financials), (_v, id) =>
  validApplicantIds.has(id),
) as ApplicantFinancials;
const financials = ref({ ...initFinancials, ...existingFinancials });
const validGroupIds = new Set((loanApp.value.borrowerGroups ?? []).map((group) => group.id));
const entityIncome = ref<EntityIncome>(
  pickBy(cloneDeep(getEntityIncome(loanApp.value)), (_v, id) => validGroupIds.has(id)),
);
for (const entry of Object.values(financials.value)) delete entry.companyIncome;

const getCompanyIncomeForBorrowerGroup = (group: BorrowerGroup): CompanyIncome | null =>
  entityIncome.value[group.id] ?? null;

const isCompanyIncomeComplete = (group: BorrowerGroup): boolean => {
  const netProfitAmount = getCompanyIncomeForBorrowerGroup(group)?.netProfit?.amount ?? 0;
  return netProfitAmount > 0;
};

/**
 * Check if all required Net Profit forms have been completed.
 * Data-driven approach: checks if actual data exists (handles refresh/edits automatically).
 */
const allNetProfitComplete = computed<boolean>(() => {
  const netProfitGroups = (loanApp.value.borrowerGroups ?? []).filter(shouldShowNetProfit);

  // If no Net Profit forms required, consider complete
  if (netProfitGroups.length === 0) return true;

  return netProfitGroups.every(isCompanyIncomeComplete);
});

/**
 * Deduplicated applicants needing individual income forms.
 * Individual trustees show immediately; directors/others show after ALL Net Profits complete.
 */
const individualIncomeApplicants = computed<ApplicantId[]>(() => {
  if (!showCommercialIncomeUI.value) return [];

  const groups = loanApp.value.borrowerGroups ?? [];

  // Separate individual trustee groups from others
  const individualTrusteeGroups = groups.filter(
    (group) =>
      group.type === BorrowerType.TRUST &&
      group.trustDetails?.trusteeType === TrusteeType.INDIVIDUAL &&
      shouldShowIndividualIncome(group),
  );

  const otherGroups = groups.filter(
    (group) =>
      !(group.type === BorrowerType.TRUST && group.trustDetails?.trusteeType === TrusteeType.INDIVIDUAL) &&
      shouldShowIndividualIncome(group),
  );

  // Individual trustees always show (not blocked by Net Profit completion)
  const individualTrusteeIds = individualTrusteeGroups.flatMap(getApplicantsForBorrowerGroup);

  // Regulated: show directors immediately
  // Unregulated: wait for ALL Net Profit complete before showing directors
  const showDirectors = !collectionRules.needsServicingCheck.value || allNetProfitComplete.value;
  const otherIds = showDirectors ? otherGroups.flatMap(getApplicantsForBorrowerGroup) : [];

  return [...new Set([...individualTrusteeIds, ...otherIds])];
});

const incomeRefs = useTemplateRefsList<InstanceType<typeof IncomeForm>>();
const companyIncomeRefs = useTemplateRefsList<InstanceType<typeof CompanyIncomeForm>>();

// Active tab can be either an ApplicantId or a BorrowerGroup id
const activeTab = ref<string | undefined>(getInitialActiveTab());

function getInitialActiveTab(): string | undefined {
  if (showCommercialIncomeUI.value) return undefined;
  return validApplicants.value.length === 1 ? validApplicants.value[0] : undefined;
}

const updateCompanyIncome = (group: BorrowerGroup, companyIncome: CompanyIncome) => {
  entityIncome.value[group.id] = companyIncome;
};

/** Handles retirement details update from the IncomeForm sub-component. */
const updateRetirementDetails = (applicantId: ApplicantId, details: RetirementDetails) => {
  if (!financials.value[applicantId]) {
    financials.value[applicantId] = createFinancials();
  }
  financials.value[applicantId].retirementDetails = details;
};

const hasIndividualIncome = (applicantId: ApplicantId): boolean => {
  return !!financials.value[applicantId]?.income?.employment?.length;
};

/** Checks if the additional income section has been answered for a given applicant. */
const hasAdditionalIncomeAnswered = (applicantId: ApplicantId): boolean => {
  return financials.value[applicantId]?.income?.shortTermChange !== undefined;
};
/** Validate refs and return index of first invalid (-1 if all valid) */
const findFirstInvalidIndex = async <T extends { update: () => Promise<boolean | undefined> }>(
  refs: T[],
): Promise<number> => {
  const results = await Promise.all(refs.map(async (ref) => (await ref?.update()) ?? false));
  return results.findIndex((isValid) => !isValid);
};

const validateIndividualIncome = async () => {
  const applicants = showCommercialIncomeUI.value ? individualIncomeApplicants.value : validApplicants.value;
  const refs = incomeRefs.value.slice(0, applicants.length);
  const invalidIndex = await findFirstInvalidIndex(refs);

  if (invalidIndex === -1) return true;

  activeTab.value = applicants[invalidIndex];
  window.scrollTo(0, 0);
  return false;
};

const validateCompanyIncome = async () => {
  const netProfitGroups = loanApp.value.borrowerGroups?.filter(shouldShowNetProfit) ?? [];
  // Slice refs to expected count to avoid accumulated refs from re-renders (useTemplateRefsList issue)
  const refs = companyIncomeRefs.value.slice(0, netProfitGroups.length);
  const invalidIndex = await findFirstInvalidIndex(refs);

  if (invalidIndex === -1) return true;

  activeTab.value = netProfitGroups[invalidIndex]?.id;
  window.scrollTo(0, 0);
  return false;
};

const validateIncome = async () => {
  if (showCommercialIncomeUI.value) {
    const companyValid = await validateCompanyIncome();
    if (!companyValid) return false;
    return validateIndividualIncome();
  }
  return validateIndividualIncome();
};

const validateAllBorrowersHaveIncome = (): boolean => {
  if (showCommercialIncomeUI.value) {
    const groups = loanApp.value.borrowerGroups ?? [];

    const hasInvalidNetProfit = groups.some((g) => shouldShowNetProfit(g) && !isCompanyIncomeComplete(g));
    if (hasInvalidNetProfit) {
      error.value = 'Each company/trust must have net profit details.';
      return false;
    }

    const hasInvalidIndividual = !individualIncomeApplicants.value.every(hasIndividualIncome);
    if (hasInvalidIndividual) {
      error.value = 'Each borrower must add at least one income source.';
      return false;
    }

    return true;
  }

  if (!validApplicants.value.every(hasIndividualIncome)) {
    error.value = 'Each applicant must add at least one income source.';
    return false;
  }
  return true;
};

const saveAndContinue = async () => {
  if (!(await validateIncome())) return;
  if (!validateAllBorrowersHaveIncome()) return;

  isLoading.value = true;
  const applicationDraftId = loanApp.value.applicationDraftId;

  try {
    error.value = '';

    return saveAndGoNext({
      financials: financials.value,
      entityIncome: entityIncome.value,
      applicationDraftId,
    });
  } catch (err) {
    if (api.getErrorMsg(err) === INVALID_JWT_ERROR) {
      flagSessionExpired();
      return;
    }
    console.error('Failed to save draft:', err);
  } finally {
    isLoading.value = false;
  }
};

const getBorrowerName = (group: BorrowerGroup): string => {
  return getBorrowerDisplayName(group, loanApp.value.applicants);
};

const getBorrowerIcon = (group: BorrowerGroup): IconName => {
  if (group.type === BorrowerType.COMPANY) return 'company';
  if (group.type === BorrowerType.TRUST) return 'trust';
  return 'customer';
};

const getCardTitle = (employment: { type: string; tradingName?: string; employerName?: string }): string => {
  const typeLabel = employment.type.charAt(0).toUpperCase() + employment.type.slice(1).replace('_', ' ');

  if (employment.type === EmploymentType.SELF_EMPLOYED) {
    return `${typeLabel}: ${employment.tradingName ?? ''}`;
  }

  const salaryTypes: string[] = [EmploymentType.FULL_TIME, EmploymentType.PART_TIME, EmploymentType.CASUAL];
  if (salaryTypes.includes(employment.type)) {
    return `${typeLabel}: ${employment.employerName ?? ''}`;
  }

  return typeLabel;
};

const getEmploymentDescriptions = (applicantId: ApplicantId): string[] => {
  return financials.value[applicantId]?.income?.employment?.map(getCardTitle) ?? [];
};

/** Checks if the retirement / financial outlook section has been answered for a given applicant. */
const hasRetirementDetailsAnswered = (applicantId: ApplicantId): boolean => {
  const details = financials.value[applicantId]?.retirementDetails;
  if (!details) return false;
  return details.financialChangesDuringLoan !== undefined;
};

const getIndividualStatusItems = (applicantId: ApplicantId) => [
  { label: 'Employment details', isComplete: hasIndividualIncome(applicantId) },
  { label: 'Additional income', isComplete: hasAdditionalIncomeAnswered(applicantId) },
  { label: 'Financial outlook', isComplete: hasRetirementDetailsAnswered(applicantId) },
];

const getCompanyStatusItems = (group: BorrowerGroup) => [
  { label: 'Net profit details', isComplete: isCompanyIncomeComplete(group) },
];

const getIncomeButtonLabel = (isComplete: boolean): string => {
  return isComplete ? 'Edit income' : 'Add income';
};

const isButtonDisabled = (tabId: string): boolean => {
  return !!activeTab.value && activeTab.value !== tabId;
};

/**
 * Handler for when company income (Net Profit) is saved.
 * Calculates netSurplus if all Net Profit forms are complete and servicing check is needed.
 * Uses data-driven completion check for reliability across refreshes and edits.
 */
const onCompanyIncomeSaved = async (): Promise<void> => {
  activeTab.value = undefined;

  // If all Net Profit complete and servicing check needed, calculate netSurplus
  if (allNetProfitComplete.value && collectionRules.needsServicingCheck.value) {
    isLoading.value = true;
    try {
      const netSurplus = await calc.getNetSurplus({
        ...loanApp.value,
        financials: financials.value,
        entityIncome: entityIncome.value,
      });

      if (netSurplus !== undefined) {
        send({
          type: 'UPDATE',
          loanApp: { ...loanApp.value, netSurplus },
        });
      }
    } catch (error) {
      console.error('Failed to calculate net surplus:', error);
    } finally {
      isLoading.value = false;
    }
  }
};
</script>

<template>
  <div class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        <template v-if="hasNonIndividualBorrower">
          Please outline the borrower's
          <BxHighlight>income</BxHighlight>
          sources?
        </template>
        <template v-else>
          What are your
          <BxHighlight>income</BxHighlight>
          sources?
        </template>
      </h2>
    </div>
    <div v-if="incomeSourcesubHeading" class="my-6">
      <p>{{ incomeSourcesubHeading }}</p>
    </div>

    <!-- Commercial Flow -->
    <div v-if="showCommercialIncomeUI" class="my-6 grid gap-4">
      <!-- Net Profit Forms for Company / Trust with Company trustee -->
      <template v-for="group in loanApp.borrowerGroups?.filter(shouldShowNetProfit)" :key="group.id">
        <BxCard :class="group.id !== activeTab && 'hidden'">
          <h2 class="mb-4 flex items-center text-xl font-medium">
            <span class="mr-3 hidden md:block">
              <BxIcon :name="getBorrowerIcon(group)" />
            </span>
            {{ getBorrowerName(group) }}
          </h2>
          <CompanyIncomeForm
            :ref="companyIncomeRefs.set"
            :model-value="getCompanyIncomeForBorrowerGroup(group)"
            :borrower-group="group"
            :applicants="loanApp.applicants"
            :show-buttons="true"
            @update:model-value="(v: CompanyIncome) => updateCompanyIncome(group, v)"
            @save="onCompanyIncomeSaved"
            @cancel="activeTab = undefined"
          />
        </BxCard>
        <IncomeStatusCard
          v-show="group.id !== activeTab"
          :id="group.id + '-add-income'"
          :title="getBorrowerName(group)"
          :icon="getBorrowerIcon(group)"
          :status-items="getCompanyStatusItems(group)"
          :button-label="getIncomeButtonLabel(isCompanyIncomeComplete(group))"
          :is-disabled="isButtonDisabled(group.id)"
          @edit="activeTab = group.id"
        />
      </template>

      <!-- Individual Income Forms (deduplicated) -->
      <div v-for="applicantId in individualIncomeApplicants" :key="applicantId">
        <BxCard :class="applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 flex items-center text-xl font-medium">
            <span class="mr-3 hidden md:block">
              <BxIcon name="customer" />
            </span>
            {{ getFullName(loanApplicants[applicantId]) }}
          </h2>
          <IncomeForm
            :ref="incomeRefs.set"
            v-model="financials[applicantId].income"
            :retirement-details="financials[applicantId]?.retirementDetails"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :show-buttons="true"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
            @update:retirement-details="(v: RetirementDetails) => updateRetirementDetails(applicantId, v)"
          />
        </BxCard>
        <IncomeStatusCard
          v-show="applicantId !== activeTab"
          :id="applicantId + '-add-income'"
          :title="getFullName(loanApplicants[applicantId])"
          icon="customer"
          :status-items="getIndividualStatusItems(applicantId)"
          :descriptions="getEmploymentDescriptions(applicantId)"
          :button-label="getIncomeButtonLabel(hasIndividualIncome(applicantId))"
          :is-disabled="isButtonDisabled(applicantId)"
          @edit="activeTab = applicantId"
        />
      </div>

      <span v-if="error" class="mt-6 block text-sm text-rose-500">{{ error }}</span>
    </div>

    <!-- Original Individual Flow -->
    <div v-else class="my-6 grid gap-4">
      <div v-for="applicantId in validApplicants" :key="applicantId">
        <BxCard :class="applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 text-xl font-medium">{{ getFullName(loanApplicants[applicantId]) }}</h2>
          <IncomeForm
            :ref="incomeRefs.set"
            v-model="financials[applicantId].income"
            :retirement-details="financials[applicantId]?.retirementDetails"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :show-buttons="validApplicants.length > 1"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
            @update:retirement-details="(v: RetirementDetails) => updateRetirementDetails(applicantId, v)"
          />
        </BxCard>
        <BxCard :class="`${applicantId === activeTab && 'hidden'} employment-card`">
          <div class="flex items-center">
            <div class="card-details">
              <div class="mb-2 font-medium">
                {{ getFullName(loanApplicants[applicantId]) }}
              </div>

              <div class="flex items-center justify-between gap-20">
                <div class="flex items-center gap-2">
                  <div class="h-5 w-5">
                    <CheckIcon v-if="financials[applicantId]?.income?.employment?.length" class="text-green-600" />
                    <XMarkIcon v-else class="text-rose-600" />
                  </div>
                  Employment details
                </div>

                <div class="text-gray-400">
                  {{ getEmploymentDescriptions(applicantId)[0] }}
                </div>
              </div>

              <div class="flex items-center justify-between">
                <div class="flex items-center gap-2">
                  <div class="h-5 w-5">
                    <CheckIcon
                      v-if="financials[applicantId]?.income?.expectIncomeChange !== undefined"
                      class="text-green-600"
                    />
                    <XMarkIcon v-else class="text-rose-600" />
                  </div>
                  Additional income
                </div>

                <div class="text-gray-400">
                  {{ getEmploymentDescriptions(applicantId)[1] }}
                </div>
              </div>
            </div>

            <div class="card-button">
              <BxButton
                :id="applicantId + '-add-income'"
                variant="secondary"
                :disabled="
                  (activeTab && activeTab !== applicantId) ||
                  (loanApplicants[applicantId]?.type === 'primary' && isSecondaryDeclaration)
                "
                @click="activeTab = applicantId"
              >
                {{ financials[applicantId]?.income?.employment?.length ? 'Edit' : 'Add' }} income
              </BxButton>
            </div>
          </div>
        </BxCard>
        <BxCard :class="`${applicantId === activeTab && 'hidden'} employment-card-mobile`">
          <div class="items-left flex flex-col">
            <div class="card-details">
              <div class="mb-2 font-medium">{{ getFullName(loanApplicants[applicantId]) }}</div>
              <div class="items-left flex gap-2">
                <div class="h-5 w-5">
                  <CheckIcon v-if="financials[applicantId]?.income?.employment?.length" class="text-green-600" />
                  <XMarkIcon v-else class="text-rose-600" />
                </div>
                Employment details
              </div>
              <div class="items-left flex gap-2">
                <div class="h-5 w-5">
                  <CheckIcon
                    v-if="financials[applicantId]?.income?.expectIncomeChange !== undefined"
                    class="text-green-600"
                  />
                  <XMarkIcon v-else class="text-rose-600" />
                </div>
                Additional income
              </div>
            </div>
            <div class="employment-descriptions">
              <div>
                <div v-for="description in getEmploymentDescriptions(applicantId)" :key="description">
                  <span class="text-gray-400">{{ description }}</span>
                </div>
              </div>
            </div>
          </div>
          <div class="card-button">
            <BxButton
              variant="secondary"
              :disabled="
                (activeTab && activeTab !== applicantId) ||
                (loanApplicants[applicantId]?.type === 'primary' && isSecondaryDeclaration)
              "
              @click="activeTab = applicantId"
            >
              {{ financials[applicantId]?.income?.employment?.length ? 'Edit' : 'Add' }} income
            </BxButton>
          </div>
        </BxCard>
      </div>

      <span v-if="error" class="mt-6 block text-sm text-rose-500">{{ error }}</span>
    </div>

    <!-- Footer Buttons -->
    <div class="mt-6">
      <BxButton
        id="income-continue"
        variant="primary"
        :loading="isLoading || isCalcProcessing"
        :disabled="isCalcProcessing"
        @click="saveAndContinue()"
      >
        {{ showCommercialIncomeUI ? 'Continue' : 'Save and continue' }}
      </BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.employment-descriptions {
  @media (max-width: 767px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}

.employment-card {
  @media (max-width: 767px) {
    display: none;
  }
}

.employment-card-mobile {
  @media (min-width: 767px) {
    display: none;
  }
}

.card-button {
  flex-grow: 1;
  flex-basis: 200px;

  @media (min-width: 767px) {
    text-align: right;
  }

  @media (max-width: 767px) {
    text-align: center;
  }
}

.card-details {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.employment-descriptions {
  display: flex;
  flex-direction: column;
  justify-content: center;

  @media (min-width: 768px) {
    min-width: 240px;
    margin-left: 32px;
  }

  @media (max-width: 767px) {
    margin-top: 20px;
    margin-bottom: 20px;
  }
}
</style>
